import { useContext, useMemo } from "react";
import { useAppSelector } from "@litres-core/store";
import {
  isTurkeyDomain,
  isRuDomain as isRussianDomain,
  isAzerbaijanDomain,
  isUzbekistanDomain,
} from "@litres/utils";
import { type LowerMenuItemType } from "../types";
import { HeaderContext, MAIN_DOMAIN } from "../../../Header";
import {
  ELowerMenuItems,
  LOWER_MENU_ITEMS,
  LOWER_MENU_ITEMS_MORE,
} from "../constants";

export const useLowerMenuItems = (isPDA: boolean) => {
  const { isLibraryDomain, isSchoolDomain, isMainDomain, isPdaDomain } =
    useContext(HeaderContext);

  const hasActiveSubscription = useAppSelector(
    state => state.foundationUser.subscription.is_active
  );
  const { library_info } = useAppSelector(state => state.foundationUser);
  const isSchoolUser = library_info.is_school_user || isSchoolDomain;
  const browser = useAppSelector(state => state.browser);
  const clientHost = browser.headers["client-host"] || MAIN_DOMAIN;
  const isTrDomain = isTurkeyDomain(clientHost);
  const isRuDomain = isRussianDomain(clientHost);
  const isAzDomain = isAzerbaijanDomain(clientHost);
  const isUzDomain = isUzbekistanDomain(clientHost);
  const lang = useAppSelector(state => state.face.language);

  return useMemo(() => {
    const items = isPDA
      ? [...LOWER_MENU_ITEMS, ...LOWER_MENU_ITEMS_MORE]
      : [...LOWER_MENU_ITEMS];

    const filterItems = (items: LowerMenuItemType[]) => {
      return items.filter(({ isMainDomainOnly, id }) => {
        /**
         * Показываем элемент со свойством isMainDomainOnly только на основном домене
         */
        if (!isMainDomain && !isPdaDomain && isMainDomainOnly) {
          return;
        }

        /**
         Не показываем пункт, если есть активная подписка или подписка в грейс-периоде
         */
        if (hasActiveSubscription && id === ELowerMenuItems.subscription) {
          return;
        }

        /**
         * Для школьников выводим усеченное меню
         */
        if (
          isSchoolUser &&
          [
            ELowerMenuItems.compilations,
            ELowerMenuItems.selfpub,
            ELowerMenuItems.subscription,
            ELowerMenuItems.promoCodes,
          ].includes(id)
        ) {
          return;
        }

        /**
         * Для библиоюзеров выводим усеченное меню
         */
        if (isLibraryDomain && [ELowerMenuItems.subscription].includes(id)) {
          return;
        }

        /**
         * Иностранные домены
         */
        if (
          lang !== "ru" &&
          [
            ELowerMenuItems.selfpub,
            ELowerMenuItems.subscription,
            ELowerMenuItems.company,
            ELowerMenuItems.compilations,
            ELowerMenuItems.becomeAnAuthor,
          ].includes(id)
        ) {
          return;
        }

        /**
         * Для турции не показываем пункт Комиксы и Подборки в меню
         */
        if (
          isTrDomain &&
          [ELowerMenuItems.comics, ELowerMenuItems.compilations].includes(id)
        ) {
          return;
        }

        /**
         * Скрываем комиксы для части иностранных доменов: NAV-2047
         */
        if (
          !(isRuDomain || isAzDomain || isUzDomain) &&
          ELowerMenuItems.comics === id
        ) {
          return;
        }

        /**
         * Только для Турции показываем:
         * "О компании"
         * "Опубликовать книгу"
         */
        if (
          !isTrDomain &&
          [
            ELowerMenuItems.company,
            ELowerMenuItems.litresLandingTrOnly,
          ].includes(id)
        ) {
          return;
        }

        return true;
      });
    };

    const menuItems = filterItems(items);
    const defaultMoreItems = filterItems(LOWER_MENU_ITEMS_MORE);

    /**
     * Только для Ru доменов:
     * Переименовываем "Комиксы" в "Комиксы и вебтуны"
     */
    if (lang === "ru") {
      menuItems.forEach(item => {
        if (item.id === ELowerMenuItems.comics) {
          item.title = "Комиксы и вебтуны";
        }
      });
    }
    return {
      menuItems,
      defaultMoreItems,
    };
  }, [
    isPDA,
    isMainDomain,
    isPdaDomain,
    isTrDomain,
    hasActiveSubscription,
    isLibraryDomain,
    isSchoolUser,
    lang,
  ]);
};
